import { useCallback, useEffect, useRef, useState } from 'react';

// Each loader generation owns its requests. GETs only; mutations are never retried here.
export function useVisibleResource(loader, interval = 0) {
  const [state, setState] = useState({ data: null, loading: true, error: null, receivedAt: null, paused: false, owner: loader });
  const control = useRef(null);
  const reload = useCallback(() => control.current?.run(), []);
  useEffect(() => {
    let alive = true, busy = false, blocked = false, timer, controller;
    const available = () => navigator.onLine !== false && document.visibilityState !== 'hidden';
    const run = async () => {
      if (!alive || busy || blocked || !loader) return;
      if (!available()) { setState(s => ({ ...s, loading: false, paused: true })); return; }
      clearTimeout(timer); busy = true; controller = new AbortController();
      setState(s => ({ ...s, loading: true, paused: false }));
      try {
        const data = await loader(controller.signal);
        if (alive) setState({ data, loading: false, error: null, receivedAt: Date.now(), paused: !available(), owner: loader });
      } catch (error) {
        if (!alive) return;
        const status = error?.response?.status;
        blocked = [401,403,404,410].includes(status);
        setState(s => ({ ...s, data: blocked ? null : s.data, loading: false,
          error: blocked ? 'unavailable' : 'temporary', paused: !available() }));
      } finally {
        busy = false;
        if (alive && !blocked && interval && available()) timer = setTimeout(run, interval);
      }
    };
    const visibility = () => {
      if (!alive) return;
      clearTimeout(timer);
      setState(s => ({ ...s, paused: !available() }));
      if (available()) run();
    };
    control.current = { run };
    setState({ data: null, loading: !!loader, error: null, receivedAt: null, paused: !available(), owner: loader });
    if (loader) run();
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('online', visibility); window.addEventListener('offline', visibility);
    return () => {
      alive = false; clearTimeout(timer); controller?.abort(); control.current = null;
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('online', visibility); window.removeEventListener('offline', visibility);
    };
  }, [loader, interval]);
  return { ...(state.owner === loader ? state : { data: null, loading: !!loader, error: null, receivedAt: null, paused: false }), reload };
}
